import React, { useState, useEffect } from 'react';
import { Input } from './ui/input';
import { Phone } from 'lucide-react';

/**
 * Phone Input component with US +1 prefix and formatting
 * Formats as: +1 (XXX) XXX-XXXX
 */
const PhoneInput = ({
  value = '',
  onChange,
  placeholder = "(555) 555-5555",
  className = "",
  disabled = false,
  id,
  "data-testid": dataTestId,
  required = false
}) => {
  const [displayValue, setDisplayValue] = useState('');

  // Format phone number for display
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    
    // Remove all non-digits
    let digits = phone.replace(/\D/g, '');
    
    // Remove leading 1 if present (we'll add +1 prefix separately)
    if (digits.startsWith('1') && digits.length > 10) {
      digits = digits.substring(1);
    }
    
    // Limit to 10 digits
    digits = digits.substring(0, 10);
    
    // Format as (XXX) XXX-XXXX
    if (digits.length === 0) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
    return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
  };

  // Get clean phone number with +1 prefix for storage
  const getStorageValue = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 0) return '';
    
    // Remove leading 1 if present
    const cleanDigits = digits.startsWith('1') && digits.length > 10 
      ? digits.substring(1) 
      : digits;
    
    // Only return with +1 if we have enough digits
    if (cleanDigits.length >= 10) {
      return `+1${cleanDigits.substring(0, 10)}`;
    }
    return cleanDigits; // Return partial during typing
  };

  // Initialize display value from prop
  useEffect(() => {
    if (value) {
      setDisplayValue(formatPhoneDisplay(value));
    } else {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e) => {
    const input = e.target.value;
    
    // Only allow digits, spaces, parentheses, and hyphens
    const cleaned = input.replace(/[^\d\s()-]/g, '');
    
    // Format for display
    const formatted = formatPhoneDisplay(cleaned);
    setDisplayValue(formatted);
    
    // Get storage value with +1 prefix
    const storageValue = getStorageValue(cleaned);
    
    // Call onChange with the +1 formatted value
    if (onChange) {
      onChange(storageValue);
    }
  };

  const handleBlur = () => {
    // On blur, ensure proper formatting
    const storageValue = getStorageValue(displayValue);
    if (onChange && storageValue !== value) {
      onChange(storageValue);
    }
  };

  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none z-10 flex items-center gap-1">
        <Phone className="h-4 w-4" />
        <span className="text-sm font-medium">+1</span>
      </div>
      <Input
        type="tel"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        className={`pl-16 ${className}`}
        disabled={disabled}
        id={id}
        data-testid={dataTestId}
        required={required}
        pattern="[\d\s()-]*"
        inputMode="tel"
      />
    </div>
  );
};

export default PhoneInput;
